<template>
  <Dialog v-model:visible="visible" :modal="true" :closable="false"
    :style="{ width: '460px', maxHeight: '90vh', overflow: 'auto' }">

    <!-- Progress Bar -->
    <div class="flex justify-between items-center mb-3">
      <div class="w-full flex items-center">
        <div v-for="step in totalSteps" :key="step" :class="['flex-1 text-center py-1 text-xs', {
          'rounded-l-lg': step === 1,
          'rounded-r-lg': step === totalSteps,
          'bg-primary text-white': currentStep >= step,
          'bg-gray-200 text-gray-500': currentStep < step
        }]">
          {{ step }}
        </div>
      </div>
    </div>

    <!-- Step content -->
    <!-- Step 1: Session Structure -->
    <div v-if="currentStep === 1">
      <CreateSessionStructureModal @success="currentStep = 2" />
    </div>

    <!-- Step 2: Create Session -->
    <div v-else-if="currentStep === 2">
      <CreateSessionModal :formData="formData" :searchQuery="searchQuery" :filteredSessions="filteredSessions"
        :existing-sessions="existingSessions" :errors="errors" @submit="handleSessionSubmit"
        @select-session="handleSessionSelect" />
    </div>

    <!-- Step 3: Create Academic Calendar -->
    <div v-else-if="currentStep === 3">
      <CreateAcademicCalendarModal :currentStep="currentStep" @update-step="(step) => currentStep = step" />
    </div>

    <!-- Step 4 (Non-tertiary): Create Section (Arm) Check -->
    <div v-else-if="currentStep === 4 && !isTertiary">
      <div class="text-center space-y-3">
        <p class="text-sm font-semibold">Do you have an Arm for this classes?</p>
        <p v-for="classroom in classrooms" :key="classroom?.id" class="text-sm flex items-center gap-1.5 justify-center">
          <span>{{ classroom?.name }}</span>
        </p>
        <div class="flex justify-center gap-2">
          <Button label="Yes" size="small" @click="handleArmYes" />
          <Button label="No" size="small" @click="handleArmNo" severity="secondary" />
        </div>
      </div>
    </div>

    <!-- Step 5 (Non-tertiary): Create Section Form -->
    <div v-else-if="currentStep === 5 && hasArm && !isTertiary">
      <CreateSectionModal @success="handleSectionSuccess" />
    </div>

    <!-- Tertiary Steps -->
    <!-- Step 4: Create Classrooms -->
    <div v-else-if="currentStep === 4 && isTertiary">
      <CreateClassroomModal @success="handleClassroomSuccess" />
    </div>

    <!-- Step 5: Create Sections Check -->
    <div v-else-if="currentStep === 5 && isTertiary && !hasArm">
      <div class="text-center space-y-3">
        <p class="text-sm font-semibold">Do you want to setup manual classroom sections?</p>
        <p class="text-xs text-gray-500">Usually tertiary institutions use simple structures, but you can add manual
          sections if needed.</p>
        <div class="flex justify-center gap-2">
          <Button label="Yes" size="small" @click="handleTertiarySectionYes" />
          <Button label="No, Use Default" size="small" @click="handleTertiarySectionNo" severity="secondary"
            :loading="isSubmitting" />
        </div>
      </div>
    </div>

    <!-- Step 5 (Tertiary Manual): Create Section Form -->
    <div v-else-if="currentStep === 5 && isTertiary && hasArm">
      <div class="mb-2 text-center text-xs text-yellow-600">
        Please configure your manual sections.
      </div>
      <CreateSectionModal @success="handleSectionSuccess" />
    </div>

    <!-- Step 6: Faculty -->
    <div v-else-if="currentStep === 6 && isTertiary">
      <CreateFacultyModal :currentStep="currentStep" @update-step="(step) => currentStep = step" />
    </div>

    <!-- Step 7: Department -->
    <div v-if="currentStep === 7 && isTertiary">
      <CreateDepartmentModal @success="currentStep = 8" />
    </div>

    <!-- Step 8: Programme -->
    <div v-else-if="currentStep === 8 && isTertiary">
      <CreateProgrammeModal @success="setupStore.closeWizard()" />
    </div>

  </Dialog>
</template>

<script setup lang="ts">
import CreateSessionStructureModal from './CreateSessionStructureModal.vue';
import CreateSessionModal from './CreateSessionModal.vue';
import CreateAcademicCalendarModal from './CreateAcademicCalendarModal.vue';
import CreateSectionModal from './CreateSectionModal.vue';
import CreateClassroomModal from './CreateClassroomModal.vue';
import CreateFacultyModal from './Tertiary/CreateFacultyModal.vue';
import CreateDepartmentModal from './Tertiary/CreateDepartmentModal.vue';
import CreateProgrammeModal from './Tertiary/CreateProgrammeModal.vue';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { useUserStore } from '@/store/userStore';
import { useSchoolMutations } from '@/composables/mutations/school/useSchoolMutations';
import { useSchoolSetupStore } from '@/store/schoolSetupStore';
import { useToast } from 'primevue/usetoast';
import { generateSessions } from '@/utils';
import { SetupStep } from '@/constants/setup-steps';
import { QUERY_ALL_SESSIONS } from '@/graphql';
import { useSessionStore } from '@/store/sessionStore';

import { extractGraphQLError } from '@/utils/graphqlError';
const emit = defineEmits(['success', 'close']);
const userStore = useUserStore();
const { createSession, createClassroomSection } = useSchoolMutations();
const setupStore = useSchoolSetupStore();
const sessionStore = useSessionStore();
const toast = useToast();

const { result: existingSessionsResult } = QUERY_ALL_SESSIONS();
const existingSessions = computed(() => existingSessionsResult.value?.schoolSessions || []);

const visible = computed({
  get: () => setupStore.isWizardOpen,
  set: (value) => {
    if (!value) setupStore.closeWizard();
  },
});

const { school, classrooms, refetchSchool } = useSchoolQuery();
const { isTertiary } = useSchoolFeatures();

const totalSteps = computed(() => isTertiary.value ? 8 : 5);

const currentStep = ref(1);
const isSubmitting = ref(false);
const hasArm = ref<boolean | null>(null);

const stepMap: Record<string, number> = {
  [SetupStep.SESSION_STRUCTURE]: 1,
  [SetupStep.SESSION]: 2,
  [SetupStep.ACADEMIC_CALENDAR]: 3,
  [SetupStep.CLASSROOMS]: 4,
  [SetupStep.TERTIARY_FACULTY]: 6,
  [SetupStep.TERTIARY_DEPARTMENT]: 7,
  [SetupStep.TERTIARY_PROGRAMME]: 8,
};

watch(() => setupStore.isWizardOpen, (isOpen) => {
  if (isOpen && setupStore.firstIncompleteStep) {
    currentStep.value = stepMap[setupStore.firstIncompleteStep] || 1;
  }
});

const formData = ref<{
  name: string;
  started_at: string | Date | null;
  ended_at: string | Date | null;
  session_structure_id: string;
}>({
  name: '',
  started_at: null,
  ended_at: null,
  session_structure_id: '',
});

const errors = ref<{ [key: string]: string }>({});
const searchQuery = ref('');
const allSessions = ref(generateSessions(-3, 10));

const filteredSessions = computed(() =>
  allSessions.value.filter((session) => session.toLowerCase().includes(searchQuery.value.toLowerCase())),
);

const validateForm = (): boolean => {
  errors.value = {};
  if (!formData.value.name) errors.value.name = 'Session name is required.';
  if (!formData.value.started_at) errors.value.started_at = 'Start date is required.';
  if (!formData.value.ended_at) errors.value.ended_at = 'End date is required.';
  if (formData.value.started_at && formData.value.ended_at) {
    if (new Date(formData.value.started_at) >= new Date(formData.value.ended_at)) {
      errors.value.ended_at = 'End date must be after start date.';
    }
  }
  return Object.keys(errors.value).length === 0;
};

const handleSessionSubmit = async() => {
  if (validateForm()) {
    isSubmitting.value = true;
    try {
      const sessionStructureId = userStore.tempSessionStructureId;
      if (!sessionStructureId) {
        toast.add({ severity: 'error', summary: 'Error', detail: 'Session Structure ID is missing. Please complete the previous step.', life: 4000 });
        isSubmitting.value = false;
        currentStep.value = 1; // Go back to step 1
        return;
      }
      formData.value.session_structure_id = sessionStructureId;
      const formattedData = {
        ...formData.value,
        started_at: formData.value.started_at
          ? new Date(formData.value.started_at).toISOString()
          : null,
        ended_at: formData.value.ended_at
          ? new Date(formData.value.ended_at).toISOString()
          : null,
      };
      await createSession(formattedData);
      if (userStore.tempSessionId) {
        currentStep.value = 3; // Move to next step
      }
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error creating session.'), life: 3000 });
    } finally {
      isSubmitting.value = false;
    }
  }
};

const handleSessionSelect = (sessionId: string) => {
  userStore.tempSessionId = sessionId;
  sessionStore.setSessionId(sessionId);
  toast.add({ severity: 'info', summary: 'Session Selected', detail: 'Proceeding with the selected session.', life: 3000 });
  currentStep.value = 3; // Move to next step
};

const handleSectionSuccess = () => {
  if (isTertiary.value) {
    currentStep.value = 6; // Go to Faculty
    hasArm.value = null; // Reset for future
  } else {
    setupStore.closeWizard();
  }
};

// Non-Tertiary Logic
const handleArmYes = () => {
  hasArm.value = true;
  currentStep.value = 5;
};

const handleArmNo = async() => {
  hasArm.value = false;
  isSubmitting.value = true;
  try {
    for (const classroom of classrooms.value || []) {
      await createClassroomSection({
        classroom_id: classroom?.id || '',
        name: '', // No section name if no 'arm'
        section_id: '', // No section if no 'arm'
      });
    }
    toast.add({ severity: 'success', summary: 'Success', detail: 'Classrooms set up successfully!', life: 3000 });
    setupStore.closeWizard();
  } catch {
    // Error intentionally not surfaced here; finally resets isSubmitting.
  } finally {
    isSubmitting.value = false;
  }
};

// Tertiary Logic
const handleClassroomSuccess = async() => {
  await refetchSchool();
  if (isTertiary.value) {
    isSubmitting.value = true;
    try {
      // Force refresh classrooms to ensure we have IDs
      for (const classroom of classrooms.value || []) {
        // Create default "empty" section
        await createClassroomSection({
          classroom_id: classroom?.id || '',
          name: '',
          section_id: '',
        });
      }
      toast.add({ severity: 'success', summary: 'Success', detail: 'Default sections created.', life: 3000 });
      currentStep.value = 6; // Go to Faculty
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Failed to create sections.'), life: 3000 });
    } finally {
      isSubmitting.value = false;
    }
  } else {
    currentStep.value = 5; // Move to Section Check
  }
};

const handleTertiarySectionYes = () => {
  hasArm.value = true;
  // Prompt will show CreateSectionModal
};

const handleTertiarySectionNo = async() => {
  hasArm.value = false;
  isSubmitting.value = true;
  try {
    // Force refresh classrooms to ensure we have IDs (though handleClassroomSuccess should have done it)
    // If classrooms is empty, this loop does nothing, which is fine.
    for (const classroom of classrooms.value || []) {
      // Create default "empty" section
      await createClassroomSection({
        classroom_id: classroom?.id || '',
        name: '',
        section_id: '',
      });
    }
    toast.add({ severity: 'success', summary: 'Success', detail: 'Default sections created.', life: 3000 });
    currentStep.value = 6; // Go to Faculty
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Failed to create sections.'), life: 3000 });
  } finally {
    isSubmitting.value = false;
  }
};

</script>
